// collector/actions.js
//
// The collector's action layer (collector-app P1-C part C; made durable in
// P2-B): EVERY tap on the collector's screens goes through `enqueue()`, is
// written to the phone, shown, and sent as ONE batch to
// `POST /collector-actions` (backend brief 2026-09-30-collector-actions).
//
// A PINIA STORE, not a composable. The queue must be one instance shared by
// the day page, the shop page and the sheets, and it must outlive them: F7
// removes the previous page on every push, so a composable's state would die
// with the page that made the tap while the batch is still on the wire.
//
// THE SEAM — keep it this way:
//   - An action is plain, serializable data:
//       { id, planId, planItemId, seq, clientAt, type, payload }
//     `id` = a uuid (the server's idempotency key), `seq` = a per-device
//     counter, `clientAt` = ISO 8601 with Z, never earlier than the previous
//     tap's (collector/ids.js). No closure, promise, component or callback is
//     ever put in the queue or the overlay.
//   - The lists live in shallowRefs and are only ever REPLACED, never pushed
//     into: their items stay raw objects, not Vue proxies, so IndexedDB takes
//     them as they are.
//   - Sending is a function of the batch: `sendActions(batch)` → the answers,
//     or "retry" / "rejected". Handling the answers is separate from it.
//   - The screens never read the queue; they read `collectorDayStore.day`,
//     which is the server's document with `overlay` re-applied on top
//     (collector/dayModel.js `rebaseDay`). So a refused tap reverts by leaving
//     the overlay, and a replayed queue shows on any base document.
//
// ON THE PHONE (P2-B, locked answers L-B, L-C, L-I; collector/db.js):
//   - WRITE FIRST: `enqueue` writes the tap to IndexedDB and only then shows it
//     and asks for a flush. Taps made in the same turn («اتجاب الباقي كله»)
//     share ONE strict transaction. A write that fails, or takes longer than
//     WRITE_WAIT_MS, does not block the collector at the shelf: the tap shows
//     and is sent from memory, is logged, and is written again with the next
//     write that succeeds.
//   - HYDRATE FIRST: every entry point (enqueue, flush, the day's reads) waits
//     for `ensureHydrated()` — the stored day, the taps not answered yet, the
//     answered taps no read has caught up with, and the cards — before any
//     network call. It never waits longer than HYDRATE_WAIT_MS on a stuck
//     IndexedDB; what arrives later is merged by id, never replaced.
//   - Every state change is written where phase 1 changed memory: the answers
//     (and the cards they make) in ONE transaction, then memory; a read that
//     catches up marks its taps `inRead` in the same transaction as the day.
//   - ONE FLUSH AT A TIME across the Chrome tab and the installed app (they
//     share this storage): an in-memory flag plus `navigator.locks`. At the
//     start of a flush the queue is reconciled with the stored rows (another
//     tab's taps join; taps another tab got answered leave).
//
// WHEN IT FLUSHES (L-C): the shared detector flipping to online, the page
// becoming visible, `pageshow`, right after every tap, and a 60 s tick while
// anything waits. The detector does NOT gate a send — the POST itself is the
// better probe (15 s timeout) — and while anything waits the detector is kept
// running. All pending taps go in `seq` order (the server orders a batch by
// (clientAt, seq), and clientAt never goes back on the phone).
//
// ANSWERS (spec Section 5; brief table): `applied` / `already_applied` leave
// the queue, the tap stays shown until a fresh read shows it; `conflict` and
// `refused` leave the queue, the line is pinned to the server's `current`, a
// conflict adds ONE card (kept in the `conflicts` store until dismissed) and a
// refusal toasts its reason. Nothing is lost to a bad transport: a network
// error, a timeout, a 5xx, a 403 that is not our API's, any other 4xx or a 200
// that is not a full set of answers keeps the whole batch, and the next try
// backs off 5 → 10 → 20 → 60 s (reset by any answered batch; the online flip,
// a tap, visibility and pageshow try at once). A 400, 413 or 422 is split in
// halves until the one tap the server cannot read is alone, and only that one
// is dropped. Our API's 403 (not a collector) drops the batch. After every
// answered batch the day is re-read.
//
// TWO QUEUES FOLLOW THIS ONE, armed by the same `start()` and waiting for the
// taps to drain: the collector's own photos (collector/photoQueue.js) and the
// shops' invoices filed on the phone (collector/invoiceQueue.js). His marks
// always go first.
import { f7 } from 'framework7-vue';
import { defineStore } from 'pinia';
import { computed, onScopeDispose, ref, shallowRef, watch } from 'vue';
import { findLine } from '@/collector/dayModel.js';
import { ACTION_STATUS, collectorStorage, sameId } from '@/collector/db.js';
import { newActionId, nextClientAt, nextSeq, seedCounters } from '@/collector/ids.js';
import { useCollectorInvoicesStore } from '@/collector/invoiceQueue.js';
import { collectorNetwork } from '@/collector/network.js';
import { useCollectorPhotosStore } from '@/collector/photoQueue.js';
import { sendPendingLeads } from '@/collector/previewLeads.js';
import {
  authPaused,
  collectorUserId,
  normalizeUserId,
  pauseForAuth,
  registerLocalWork,
  sessionAlive,
} from '@/collector/session.js';
import { usePwa } from '@/composables/usePwa';
import i18n from '@/i18n';
import { MAX_ACTIONS_PER_BATCH, postCollectorActions } from '@/services/collectorActions.service.js';
import { probeSession } from '@/services/collectorDay.service.js';
import { shopKey, useCollectorDayStore } from '@/stores/collectorDayStore.js';

/**
 * The seven tap types the backend takes (CollectorActionApplier::TYPES).
 * `bought` and `not_found` (P3-C, L-F) are REPORTS on her answer — like `facts`,
 * not marks: they change no outcome and may follow each other on a line.
 */
export const ACTION_TYPES = Object.freeze({
  COLLECTED: 'collected',
  OUT_OF_STOCK: 'out_of_stock',
  REOPEN: 'reopen',
  FACTS: 'facts',
  SHOP_LEFT: 'shop_left',
  BOUGHT: 'bought',
  NOT_FOUND: 'not_found',
});

/** The taps that set a line's mark (at most one of them in the air per line). */
const MARK_TYPES = new Set([ACTION_TYPES.COLLECTED, ACTION_TYPES.OUT_OF_STOCK, ACTION_TYPES.REOPEN]);

/** Refusal reasons with their own toast; anything else reads as `error`. */
const REFUSAL_REASONS = new Set([
  'plan_closed',
  'plan_cancelled',
  'plan_not_started',
  'not_collector',
  'photo_cap',
  'invalid_photo',
  'no_answered_question',
  'not_found',
  'invalid',
  'error',
]);

/**
 * Refusals that only need the day read again (brief 2026-09-30-collector-answer-on-card:
 * «اشتريته» on a line with no answered question — she has not answered, or the
 * leader already closed it). The refetch after every answered batch shows why, so
 * the collector gets the toast and NO card to dismiss.
 */
const REFRESH_ONLY_REASONS = new Set(['no_answered_question']);

/** The wait before each retry after a failed batch (L-C), capped at the last. */
export const BACKOFF_MS = Object.freeze([5_000, 10_000, 20_000, 60_000]);
/** The safety tick while anything waits (L-C). */
export const TICK_MS = 60_000;
/** How long a tap waits for its disk write before it shows anyway. */
export const WRITE_WAIT_MS = 1_500;
/** How long the first read waits for the stored day before it goes to the network. */
export const HYDRATE_WAIT_MS = 2_500;
/** While paused on a login, ask the session at most this often (a tap, the tick). */
const AUTH_PROBE_MS = 15_000;
/** When another tab holds the flush, look again after this. */
const LOCK_RETRY_MS = 3_000;
const FLUSH_LOCK = 'poolit-collector-flush';

/** Statuses the server can send per action, as stored on the row. */
const RESULT_STATUS = {
  applied: ACTION_STATUS.APPLIED,
  already_applied: ACTION_STATUS.ALREADY_APPLIED,
  conflict: ACTION_STATUS.CONFLICT,
};

/**
 * Build a tap. Plain data only — this object is what goes to disk.
 * `planItemId` is the line's `id` from the day read (the backend validates an
 * integer ≥ 1); anything else is a bug in the caller, refused here rather
 * than turned into a 422 that would hold up the taps around it.
 *
 * @returns {{id: string, planId: string|null, planItemId: string, seq: number, clientAt: string, type: string, payload: object}}
 */
export function buildAction({ type, planItemId, payload = {}, planId = null }) {
  const lineId = String(planItemId ?? '');
  if (!/^[1-9]\d*$/.test(lineId)) throw new TypeError(`collector action: bad planItemId "${lineId}"`);
  if (!Object.values(ACTION_TYPES).includes(type)) throw new TypeError(`collector action: bad type "${type}"`);
  return {
    id: newActionId(),
    planId: planId == null ? null : String(planId),
    planItemId: lineId,
    seq: nextSeq(),
    clientAt: nextClientAt(),
    type,
    payload: JSON.parse(JSON.stringify(payload ?? {})),
  };
}

/** The tap as the action layer holds it in memory (the row's action fields). */
function actionOf(row) {
  const { id, planId, planItemId, seq, clientAt, type, payload } = row;
  return { id, planId, planItemId, seq, clientAt, type, payload };
}

/** The tap as it is stored (collector/db.js `actions`). */
function rowOf(action, userId, extra = {}) {
  return {
    ...actionOf(action),
    userId,
    status: ACTION_STATUS.PENDING,
    attempts: 0,
    lastError: null,
    current: null,
    answeredAt: null,
    inRead: false,
    ...extra,
  };
}

const bySeq = (a, b) => (a.seq ?? 0) - (b.seq ?? 0);

// ── Sending: (batch) → answers ────────────────────────────────────────────────

/**
 * THE rule for "this answer is our API's": Laravel answers in JSON. A proxy, a
 * WAF or a captive portal answers with a page — the network talking, never a
 * verdict on what the collector sent. Shared with the invoice queue
 * (collector/invoiceQueue.js); do not write it a second time.
 */
export function isOurApi(response) {
  const headers = response?.headers;
  const type = typeof headers?.get === 'function' ? headers.get('content-type') : headers?.['content-type'];
  return /json/i.test(String(type ?? ''));
}

/**
 * Our API refused a TAP — not the request, and not a proxy's page (nginx's
 * «400 Request Header Or Cookie Too Large», a 413 from a gateway): only then
 * may a batch be split down and a tap dropped. A 422 must name an action
 * (`errors["actions.N.…"]`); a batch-level 422 is not about any one tap.
 */
function refusesTaps(response, status) {
  if (!isOurApi(response)) return false;
  if (status !== 422) return true;
  const errors = response?.data?.errors;
  return errors != null && typeof errors === 'object' && Object.keys(errors).some((key) => /^actions\.\d+/.test(key));
}

/**
 * Send one batch and say what came back — no state touched here.
 *   { kind: 'answered', answers }        one answer per action, every id present
 *   { kind: 'retry', offline, schedule } send the same batch again later
 *   { kind: 'paused', status }           401 / 419: keep the batch until the login is back
 *   { kind: 'rejected', status }         our API's 403 (not a collector), or its
 *                                        400 / 413 / 422 naming a tap it cannot take
 *
 * "Online" is fed from the outcome: any HTTP answer means our server answers,
 * a network error or a timeout (no response) means it does not.
 */
export async function sendActions(batch, { post = postCollectorActions, network = collectorNetwork() } = {}) {
  let answers;
  try {
    answers = await post(batch);
  } catch (error) {
    const status = error?.response?.status;
    if (!status) {
      network.report(false);
      return { kind: 'retry', offline: true, schedule: true };
    }
    network.report(true);
    if ((status === 400 || status === 413 || status === 422) && refusesTaps(error.response, status)) {
      return { kind: 'rejected', status };
    }
    // A 403 from a proxy, a WAF or a captive portal is not the API saying
    // "not a collector" — only our API's JSON may drop a day's taps.
    if (status === 403 && isOurApi(error.response)) return { kind: 'rejected', status };
    // The login expired (L-D): keep the batch — the queue pauses until the
    // session is back.
    if (status === 401 || status === 419) return { kind: 'paused', status };
    // 5xx, a 4xx that is not our API's, 404, 405, 408, 429 — a deploy, a
    // proxy or a throttle: keep every tap and try again later.
    return { kind: 'retry', offline: false, schedule: true };
  }

  network.report(true);
  // A 200 that is not one answer per sent tap (a captive portal's page, a
  // misrouted proxy) must never empty the queue.
  const answered = new Set(Array.isArray(answers) ? answers.map((answer) => answer?.id) : []);
  if (!batch.every((action) => answered.has(action.id))) {
    return { kind: 'retry', offline: false, schedule: true };
  }
  return { kind: 'answered', answers };
}

function toast(text) {
  try {
    f7?.toast?.create({ text, position: 'center', closeTimeout: 3500 }).open();
  } catch {
    /* no F7 app (tests, early boot): the state change still happened */
  }
}

const t = (key, params) => i18n.global.t(key, params);

function wait(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

// ── The store ─────────────────────────────────────────────────────────────────

export const useCollectorActionsStore = defineStore('collectorActions', () => {
  /** Taps not sent yet, in seq order. */
  const queue = shallowRef([]);
  /** Taps taken off the queue for the batch on the wire (and its halves). */
  const inflight = shallowRef([]);
  /** Answered taps no read has caught up with: `{id, planId, seq, planItemId, action, current, epoch}`. */
  const settled = shallowRef([]);
  /** Lines pinned to the server's answer after a conflict or refusal: `{id, planId, seq, planItemId, current, epoch}`. */
  const pinned = shallowRef([]);
  /** Conflict cards for the day page (spec 2.4) until the collector dismisses them. */
  const conflicts = shallowRef([]);
  /** Client time the last batch was answered. */
  const lastSyncAt = ref(null);
  /** Bumps each time a batch's answers land; a read that STARTED after an entry's epoch contains it. */
  const epoch = ref(0);
  const flushing = ref(false);
  /** When the next timed retry may go (0 = no wait). */
  const nextAttemptAt = ref(0);
  /** The flush engine's triggers are armed (`start()`). */
  const started = ref(false);

  /** Taps the server has not answered yet — the pill's count. */
  const pendingCount = computed(() => queue.value.length + inflight.value.length);

  /** Everything the day must show on top of the server's document (plain data). */
  const overlay = computed(() => [
    ...settled.value,
    ...pinned.value,
    ...inflight.value.map((action) => ({ seq: action.seq, planItemId: action.planItemId, action })),
    ...queue.value.map((action) => ({ seq: action.seq, planItemId: action.planItemId, action })),
  ]);

  /** Unanswered taps per shop key (the offline day page's «3 علامات مستنية»). */
  const waitingByShop = computed(() => {
    const { day } = useCollectorDayStore();
    const counts = {};
    for (const action of [...inflight.value, ...queue.value]) {
      const hit = findLine(day, action.planItemId);
      let key = null;
      if (hit) key = shopKey(hit.shop);
      else if (action.type === ACTION_TYPES.SHOP_LEFT) key = String(action.payload?.supplierId);
      if (key != null) counts[key] = (counts[key] ?? 0) + 1;
    }
    return counts;
  });

  const storage = () => collectorStorage();

  // ── Hydration ──────────────────────────────────────────────────────────────

  /** The user whose rows are in memory (undefined = nobody hydrated yet). */
  let hydratedFor;
  let hydration = null;
  /** Ids answered in this session — a stale read of the store must not bring them back. */
  const answeredIds = new Set();

  function ids(list) {
    return new Set(list.map((entry) => entry.id));
  }

  /**
   * Merge stored rows into memory, by id — never replacing what memory holds.
   * Pending rows memory does not know join the queue (another tab's taps, or a
   * slow first read); rows another tab got answered move out of the queue.
   */
  function mergeRows(rows = [], cards = []) {
    const inQueue = ids(queue.value);
    const inFlight = ids(inflight.value);
    const known = new Set([
      ...inQueue,
      ...inFlight,
      ...ids(settled.value),
      ...ids(pinned.value),
      ...answeredIds,
      ...enqueuing,
    ]);
    const addQueue = [];
    const addSettled = [];
    const addPinned = [];
    const leaveQueue = new Set();

    for (const row of rows) {
      if (!row?.id) continue;
      const answered = row.status && row.status !== ACTION_STATUS.PENDING;
      if (known.has(row.id)) {
        // Answered by another tab while this one still holds it as waiting.
        if (answered && inQueue.has(row.id) && !inFlight.has(row.id)) leaveQueue.add(row.id);
        else continue;
      }
      if (!answered) {
        addQueue.push(actionOf(row));
        continue;
      }
      answeredIds.add(row.id);
      if (row.inRead) continue;
      const entry = { id: row.id, planId: row.planId, seq: row.seq, planItemId: row.planItemId, current: row.current ?? null, epoch: 0 };
      if (row.status === ACTION_STATUS.APPLIED || row.status === ACTION_STATUS.ALREADY_APPLIED) {
        addSettled.push({ ...entry, action: actionOf(row) });
      } else if (row.current) {
        addPinned.push(entry);
      }
    }

    if (addQueue.length || leaveQueue.size) {
      queue.value = [...queue.value.filter((action) => !leaveQueue.has(action.id)), ...addQueue].sort(bySeq);
    }
    if (addSettled.length) settled.value = [...settled.value, ...addSettled];
    if (addPinned.length) pinned.value = [...pinned.value, ...addPinned];

    const knownCards = ids(conflicts.value);
    const newCards = cards.filter((card) => card?.id && !knownCards.has(card.id)).map(cardOf);
    if (newCards.length) {
      conflicts.value = [...conflicts.value, ...newCards].sort((a, b) => String(a.syncedAt).localeCompare(String(b.syncedAt)));
    }
  }

  /** What the phone holds, into memory: counters seeded, rows merged, the day shown if none is. */
  function applyStored(stored) {
    const rows = stored?.actions ?? [];
    seedCounters({
      seq: Math.max(0, ...rows.map((row) => Number(row.seq) || 0)),
      clientAt: Math.max(0, ...rows.map((row) => Date.parse(row.clientAt) || 0)),
    });
    mergeRows(rows, stored?.conflicts ?? []);
    useCollectorDayStore().hydrateDays(stored?.days ?? []);
  }

  /** Resolves true when the phone answered (a failed read is retried by the next call). */
  async function hydrate(userId) {
    let stored;
    try {
      stored = await storage().load(userId);
    } catch (error) {
      console.error('[collector/actions] reading the stored day failed', error);
      return false;
    }
    if (hydratedFor !== userId) return true; // another user signed in meanwhile
    applyStored(stored);
    return true;
  }

  /** A different user on this phone: forget the previous one's day in memory. */
  function resetMemory() {
    clearTimers();
    queue.value = [];
    inflight.value = [];
    settled.value = [];
    pinned.value = [];
    conflicts.value = [];
    answeredIds.clear();
    unsaved.clear();
    unsavedVerdicts.clear();
    enqueuing.clear();
    markingLines.clear();
    lastSyncAt.value = null;
    useCollectorDayStore().clear();
  }

  /**
   * The stored day and taps in memory before anything goes to the network.
   * Memoized per user; never rejects; never waits past HYDRATE_WAIT_MS.
   */
  function ensureHydrated() {
    const userId = collectorUserId();
    // Nobody signed in for a moment (between a 401 and the hold, or another
    // tab's login clearing it): this phone's collector stays in memory —
    // memory is only ever reset for a DIFFERENT signed-in user.
    if (userId == null && hydratedFor != null) return hydration ?? Promise.resolve();
    if (hydration && hydratedFor === userId) return hydration;
    if (hydratedFor !== undefined && hydratedFor !== userId) resetMemory();
    hydratedFor = userId;
    const work = hydrate(userId);
    const current = Promise.race([work, wait(HYDRATE_WAIT_MS)]);
    hydration = current;
    work
      .then((ok) => {
        if (!ok && hydration === current) hydration = null; // the next call reads again
        return ok;
      })
      .catch(() => false);
    return current;
  }

  // ── Writing taps (write first, coalesced) ──────────────────────────────────

  /** Taps whose disk write has not landed (re-put with the next write): id → row. */
  const unsaved = new Map();
  /** Taps between `enqueue()` and the queue — a reconcile must not add them twice. */
  const enqueuing = new Set();
  /**
   * Lines whose mark is being written. The screen shows a tap only once it is
   * on disk, so for those few milliseconds the line still reads unmarked: a
   * second mark on it in that window (a quick ✗ then «اتجاب الباقي كله») is
   * ignored, never sent as a contradicting pair.
   */
  const markingLines = new Set();
  let writeBatch = null;

  /** Answers whose disk write failed (re-put before anything else): id → row. */
  const unsavedVerdicts = new Map();

  /**
   * Put on disk what only memory holds: failed answers first (a lost verdict
   * would send a refused or conflicting tap again after a restart — the
   * server keeps a receipt only for applied taps), then taps whose write
   * failed. At the start of every flush, and when the page is hidden or left.
   */
  async function writeUnsaved() {
    if (unsavedVerdicts.size) {
      const rows = [...unsavedVerdicts.values()];
      try {
        await storage().putVerdicts(rows);
        rows.forEach((row) => {
          if (unsavedVerdicts.get(row.id) === row) unsavedVerdicts.delete(row.id);
        });
      } catch (error) {
        console.warn('[collector/actions] answers still not on the phone', error);
      }
    }
    if (unsaved.size) await writeRows([...unsaved.values()]);
  }

  /** One strict transaction for these pending rows; on failure they stay in `unsaved`. */
  async function writeRows(rows) {
    try {
      await storage().putActions(rows);
      rows.forEach((row) => unsaved.delete(row.id));
    } catch (error) {
      // Answered meanwhile: its verdict row supersedes this pending one.
      rows.filter((row) => !answeredIds.has(row.id)).forEach((row) => unsaved.set(row.id, row));
      console.error('[collector/actions] a tap was not written to the phone — kept in memory', error);
    }
  }

  /**
   * Write a new tap; resolves when it is on disk, or after WRITE_WAIT_MS,
   * or on a failure (logged) — never rejects, so a tap is never lost to its
   * own write. Every tap asked for in the same turn shares one transaction.
   */
  function persistNew(action) {
    if (!writeBatch) {
      const batch = { rows: [] };
      batch.done = Promise.resolve().then(() => {
        writeBatch = null;
        const rows = [...new Map([...unsaved.values(), ...batch.rows].map((row) => [row.id, row])).values()];
        const written = writeRows(rows);
        return Promise.race([written, wait(WRITE_WAIT_MS)]);
      });
      writeBatch = batch;
    }
    const row = rowOf(action, hydratedFor ?? null);
    unsaved.set(row.id, row);
    writeBatch.rows.push(row);
    return writeBatch.done;
  }

  /**
   * THE entry for every tap: build it, write it, show it, send it. Resolves
   * with the action, or null for a second mark on a line whose first is
   * still being written.
   * @param {{ type: string, planItemId: string|number, payload?: object }} tap
   */
  async function enqueue({ type, planItemId, payload = {} }) {
    await ensureHydrated();
    const dayStore = useCollectorDayStore();
    const planId = dayStore.activePlanId ?? dayStore.serverDay?.id ?? null;
    const lineId = String(planItemId ?? '');
    const isMark = MARK_TYPES.has(type);
    if (isMark && markingLines.has(lineId)) return null;
    const action = buildAction({ type, planItemId, payload, planId });
    enqueuing.add(action.id);
    if (isMark) markingLines.add(lineId);
    try {
      await persistNew(action);
      if (!queue.value.some((queued) => queued.id === action.id)) {
        queue.value = [...queue.value, action].sort(bySeq);
      }
    } finally {
      enqueuing.delete(action.id);
      if (isMark) markingLines.delete(lineId);
    }
    flush({ reason: 'tap' });
    return action;
  }

  // ── The flush engine ───────────────────────────────────────────────────────

  let failures = 0;
  let backoffTimer = null;
  let lockTimer = null;
  let tickTimer = null;
  let again = false;

  function clearTimers() {
    clearTimeout(backoffTimer);
    clearTimeout(lockTimer);
    backoffTimer = null;
    lockTimer = null;
  }

  function scheduleBackoff() {
    failures += 1;
    const delay = BACKOFF_MS[Math.min(failures, BACKOFF_MS.length) - 1];
    nextAttemptAt.value = Date.now() + delay;
    clearTimeout(backoffTimer);
    backoffTimer = setTimeout(() => {
      backoffTimer = null;
      flush({ reason: 'retry' });
    }, delay);
  }

  function resetBackoff() {
    failures = 0;
    nextAttemptAt.value = 0;
    clearTimeout(backoffTimer);
    backoffTimer = null;
  }

  /** Run `work` holding the one flush lock across tabs; false when another tab holds it. */
  async function withFlushLock(work) {
    const locks = typeof navigator === 'undefined' ? null : navigator.locks;
    if (typeof locks?.request !== 'function') {
      await work();
      return true;
    }
    let ran = false;
    await locks.request(FLUSH_LOCK, { ifAvailable: true }, async (lock) => {
      if (!lock) return;
      ran = true;
      await work();
    });
    return ran;
  }

  /** Pending rows another tab wrote, and answers it got, into memory. */
  async function reconcile() {
    try {
      applyStored(await storage().load(hydratedFor ?? null));
    } catch (error) {
      console.warn('[collector/actions] reconcile with the stored taps failed', error);
    }
  }

  /**
   * Send what waits. `reason` is only for the timed paths: the 60 s tick
   * respects the backoff; the online flip, a tap, visibility, pageshow and the
   * backoff timer itself try at once. After every attempt, the leads of an
   * offline supplier preview ride the same trigger (collector/previewLeads.js,
   * supplier-preview-offline L-L) — they name no line, so they are not taps.
   */
  async function flush({ reason = 'tap' } = {}) {
    try {
      await flushTaps(reason);
    } finally {
      sendPendingLeads({ reason });
    }
  }

  async function flushTaps(reason) {
    await ensureHydrated();
    if (authPaused.value) {
      // Nothing is sent while the login is out; the trigger asks the session
      // instead (a 401 there reaches the one shared handler — L-D).
      if (pendingCount.value > 0 || reason !== 'tap') probeAuth({ force: reason !== 'tap' && reason !== 'tick' });
      return;
    }
    if (flushing.value) {
      again = true;
      return;
    }
    // The timed paths and a resume wait out the backoff (a 419 that keeps
    // coming back on a live session must not loop); the rest try at once.
    if ((reason === 'tick' || reason === 'resume') && Date.now() < nextAttemptAt.value) return;

    flushing.value = true;
    // Whether the last round ended answered: a flush asked for meanwhile runs
    // at once only then — after a kept batch the backoff decides.
    let clean = true;
    try {
      const ran = await withFlushLock(async () => {
        do {
          again = false;
          clean = await flushRounds();
        } while (again && clean);
      });
      if (!ran && !lockTimer) {
        lockTimer = setTimeout(() => {
          lockTimer = null;
          flush({ reason: 'lock' });
        }, LOCK_RETRY_MS);
      }
    } catch (error) {
      clean = false;
      console.error('[collector/actions] flush failed', error);
    } finally {
      flushing.value = false;
    }
    // Asked for between the last round and the lock's release: not lost.
    if (again && clean) {
      again = false;
      flush({ reason: 'again' });
    }
  }

  function without(list, sent) {
    const sentIds = ids(sent);
    return list.filter((action) => !sentIds.has(action.id));
  }

  /**
   * The queue, at most 200 taps per request, inside one reload hold (a
   * waiting service-worker update cannot reload the page mid-batch). Taps made
   * while a batch is on the wire ride the next request. Resolves false when a
   * batch was kept for a retry.
   */
  async function flushRounds() {
    await writeUnsaved();
    await reconcile();
    if (queue.value.length === 0) return true;

    const network = collectorNetwork();
    const releaseReloadHold = usePwa().holdReload('collector-actions');
    let answered = false;
    let kept = false;
    try {
      rounds: while (queue.value.length > 0) {
        const round = queue.value.slice(0, MAX_ACTIONS_PER_BATCH);
        queue.value = queue.value.slice(round.length);
        inflight.value = round;
        const parts = [round];

        while (parts.length > 0) {
          const batch = parts.shift();
          let outcome = await sendActions(batch, { network });
          // Our 403 drops a batch only when it is THIS collector's session that
          // is refused: a different account signed in on another tab (they
          // share cookies) must not throw his day away — that is a login to
          // put right, so it pauses.
          if (outcome.kind === 'rejected' && outcome.status === 403 && !(await sameCollectorSignedIn())) {
            outcome = { kind: 'paused', status: 403 };
          }

          if (outcome.kind === 'retry' || outcome.kind === 'paused') {
            // Everything not answered yet goes back to the head, in seq order.
            queue.value = [...inflight.value, ...queue.value].sort(bySeq);
            inflight.value = [];
            if (outcome.kind === 'paused') {
              pauseForAuth();
              // Backed off too: a 419 that keeps coming back on a live
              // session must not loop pause → probe → resume → send.
              scheduleBackoff();
              // The interceptors' CSRF retry swallows the bus event on a
              // 419 → 401 send: a plain read settles who is signed in.
              probeAuth({ force: true });
            } else if (outcome.schedule) {
              scheduleBackoff();
            }
            kept = true;
            break rounds;
          }

          if (outcome.kind === 'rejected' && outcome.status !== 403 && batch.length > 1) {
            const half = Math.ceil(batch.length / 2);
            parts.unshift(batch.slice(0, half), batch.slice(half));
            continue;
          }

          const settle =
            outcome.kind === 'answered' ? await applyAnswers(batch, outcome.answers) : await rejectBatch(batch, outcome.status);
          // Memory moves in one step: the tap leaves the wire and lands where
          // its answer puts it in the same tick — no frame shows it unmarked.
          settle();
          inflight.value = without(inflight.value, batch);
          answered = true;
          resetBackoff();
        }
      }
    } finally {
      // Whatever broke mid-round, a tap taken off the queue never stays stranded on the wire.
      if (inflight.value.length) {
        queue.value = [...inflight.value, ...queue.value].sort(bySeq);
        inflight.value = [];
      }
      releaseReloadHold();
    }

    // A batch put back for a retry is NOT re-sent from here — only by the
    // backoff timer, the detector flipping back online, or the next tap.
    if (answered) useCollectorDayStore().refresh();
    return !kept;
  }

  /**
   * Handle one batch's answers (spec Section 5): write the verdicts and the
   * cards in ONE transaction, then return the memory change for the caller to
   * apply with the batch leaving the wire.
   */
  async function applyAnswers(batch, answers) {
    const dayStore = useCollectorDayStore();
    const byId = new Map((answers ?? []).map((answer) => [answer?.id, answer]));
    const syncedAt = new Date().toISOString();
    epoch.value += 1;
    const landed = epoch.value;
    const nextSettled = [];
    const nextPinned = [];
    const nextCards = [];
    const refusals = [];
    const refusedTaps = [];
    const rows = [];

    for (const action of batch) {
      const answer = byId.get(action.id);
      const result = answer?.result;
      const current = answer?.current ?? null;
      const entry = { id: action.id, planId: action.planId, seq: action.seq, planItemId: action.planItemId, current, epoch: landed };

      if (result === 'applied' || result === 'already_applied') {
        nextSettled.push({ ...entry, action });
        rows.push(rowOf(action, hydratedFor ?? null, { status: RESULT_STATUS[result], current, answeredAt: syncedAt }));
        continue;
      }

      if (current) nextPinned.push(entry);

      if (result === 'conflict') {
        nextCards.push(conflictCard(action, answer, dayStore.day, syncedAt));
        rows.push(rowOf(action, hydratedFor ?? null, { status: ACTION_STATUS.CONFLICT, current, answeredAt: syncedAt, lastError: answer?.state ?? null }));
        continue;
      }

      // refused (or a result this app does not know): the tap is dropped.
      const reason = REFUSAL_REASONS.has(answer?.reason) ? answer.reason : 'error';
      if (!refusals.includes(reason)) refusals.push(reason);
      if (!REFRESH_ONLY_REASONS.has(reason)) refusedTaps.push({ action, reason });
      rows.push(rowOf(action, hydratedFor ?? null, { status: ACTION_STATUS.REFUSED, current, answeredAt: syncedAt, lastError: reason }));
    }

    // A refusal can land while the phone is in a pocket (a replay after a day
    // offline): a toast would be lost, so it is kept as a card too — ONE per
    // plan and reason, listing its lines.
    nextCards.push(...refusalCards(refusedTaps, dayStore.day, syncedAt, conflicts.value));
    const cards = nextCards.length ? mergeCards(conflicts.value, nextCards) : conflicts.value;
    const kept = ids(cards);
    const dropCardIds = conflicts.value.filter((card) => !kept.has(card.id)).map((card) => card.id);
    const cardRows = nextCards.filter((card) => kept.has(card.id)).map((card) => ({ ...card, userId: hydratedFor ?? null }));

    // Before the write: a later tap's write must not put these back as pending.
    for (const action of batch) {
      answeredIds.add(action.id);
      unsaved.delete(action.id);
    }
    try {
      await storage().putVerdicts(rows, { cards: cardRows, dropCardIds });
    } catch (error) {
      // Kept and written again before anything else (writeUnsaved): an
      // applied tap resent after a restart is absorbed by the server's
      // receipt, a conflict or a refusal is judged again.
      rows.forEach((row) => unsavedVerdicts.set(row.id, row));
      console.error('[collector/actions] writing the answers to the phone failed', error);
    }

    return () => {
      settled.value = [...settled.value, ...nextSettled];
      pinned.value = [...pinned.value, ...nextPinned];
      // Merged over the cards as they are NOW: one added or dismissed while
      // the answers were being written (`addCard`, a dismiss) is not undone.
      if (nextCards.length) conflicts.value = mergeCards(conflicts.value, nextCards);
      lastSyncAt.value = syncedAt;
      if (refusals.length) toast(refusals.map((reason) => t(`collector.refused.${reason}`)).join('\n'));
    };
  }

  /**
   * Our API's 403: not a collector (the batch is dropped). A 400/413/422 on
   * ONE tap: the server cannot read it (that tap is dropped). Stored as
   * refused with the status, then said once.
   */
  async function rejectBatch(batch, status) {
    const reason = status === 403 ? 'not_collector' : 'invalid';
    const answeredAt = new Date().toISOString();
    const rows = batch.map((action) =>
      rowOf(action, hydratedFor ?? null, { status: ACTION_STATUS.REFUSED, answeredAt, lastError: String(status), inRead: true }),
    );
    const newCards = refusalCards(
      batch.map((action) => ({ action, reason })),
      useCollectorDayStore().day,
      answeredAt,
      conflicts.value,
    );
    for (const action of batch) {
      answeredIds.add(action.id);
      unsaved.delete(action.id);
    }
    try {
      await storage().putVerdicts(rows, { cards: newCards.map((card) => ({ ...card, userId: hydratedFor ?? null })) });
    } catch (error) {
      rows.forEach((row) => unsavedVerdicts.set(row.id, row));
      console.error('[collector/actions] writing a refused batch to the phone failed', error);
    }
    return () => {
      conflicts.value = mergeCards(conflicts.value, newCards); // over the cards as they are now
      lastSyncAt.value = answeredAt;
      toast(t(`collector.refused.${reason}`));
    };
  }

  /**
   * A day read of `planId` that STARTED at `readEpoch` landed: everything of
   * that plan answered before it is in that document, so it leaves the
   * overlay. Returns the ids that left, for the day store to mark `inRead` in
   * the same transaction as the document. Plan-scoped: a read of the second
   * batch never drops the first batch's taps.
   */
  function pruneThrough(readEpoch, planId = null) {
    const caughtUp = (entry) => entry.epoch <= readEpoch && (planId == null || entry.planId == null || sameId(entry.planId, planId));
    const gone = [...settled.value, ...pinned.value].filter(caughtUp).map((entry) => entry.id);
    if (gone.length) {
      settled.value = settled.value.filter((entry) => !caughtUp(entry));
      pinned.value = pinned.value.filter((entry) => !caughtUp(entry));
    }
    return gone;
  }

  function dismissConflict(id) {
    conflicts.value = conflicts.value.filter((card) => card.id !== id);
    storage()
      .deleteConflict(id)
      .catch((error) => console.warn('[collector/actions] dismissing a card on the phone failed', error));
  }

  /**
   * Her answers a read has just brought (P3-C, L-G): ONE card per answered
   * question on the day page, kept — on the phone too — until dismissed. They
   * live beside the conflict cards (the same collection and store, dismissed the
   * same way) but say a different thing, so the day page draws them apart
   * (`state: 'answered'`). A card for a line that already has one REPLACES it:
   * when she changes her answer the older card would only say something false.
   * Not announced twice: the day store only offers answers that are new against
   * the document it already held, and a card id carries `respondedAt`.
   *
   * @param {Array<object>} incoming - answer cards (stores/collectorDayStore.js `answerCard`)
   */
  function announceAnswers(incoming) {
    const known = ids(conflicts.value);
    const fresh = incoming.filter((card) => card?.id && !known.has(card.id));
    if (fresh.length === 0) return;
    const sameLine = (a, b) => String(a.planId) === String(b.planId) && String(a.planItemId) === String(b.planItemId);
    const superseded = conflicts.value.filter(
      (card) => card.state === 'answered' && fresh.some((next) => sameLine(card, next)),
    );
    const gone = ids(superseded);
    conflicts.value = [...conflicts.value.filter((card) => !gone.has(card.id)), ...fresh];
    storage()
      .putVerdicts([], {
        cards: fresh.map((card) => ({ ...card, userId: hydratedFor ?? null })),
        dropCardIds: [...gone],
      })
      .catch((error) => console.warn('[collector/actions] keeping an answer card on the phone failed', error));
  }

  /**
   * A card that is not a tap's answer (collector/invoiceQueue.js: a shop's
   * invoice the leader overruled or the server refused). Same collection, same
   * store on the phone and same dismiss as the conflict cards; a card with the
   * same id is REPLACED, so a shop has one card whatever was answered last.
   * Never rejects; resolves true when the card is on the phone.
   *
   * @param {{ id: string, planId: string|null, state: string }} card
   */
  async function addCard(card) {
    if (!card?.id) return false;
    await ensureHydrated(); // the card is stored under the collector it is for
    conflicts.value = [...conflicts.value.filter((held) => held.id !== card.id), card];
    try {
      await storage().putVerdicts([], { cards: [{ ...card, userId: hydratedFor ?? null }] });
      return true;
    } catch (error) {
      console.warn('[collector/actions] keeping a card on the phone failed', error);
      return false;
    }
  }

  /**
   * "A tap of his still waits" — for the queue that must go AFTER his marks
   * (collector/invoiceQueue.js: «المحل مادّاش فاتورة» stores his spend as the
   * server computes it when the filing ARRIVES). Memory alone does not know
   * the taps the other container wrote (the Chrome tab and the installed app
   * share this storage), so the phone is read when memory says "none"; a tap
   * still being written counts too. Taps found that way are this container's
   * to send as well — their draining is what lets the follower go. Never
   * rejects; a phone that cannot be read answers from memory.
   */
  async function tapsWaiting() {
    await ensureHydrated();
    const waiting = () => pendingCount.value > 0 || enqueuing.size > 0 || flushing.value;
    if (waiting()) return true;
    await reconcile();
    if (!waiting()) return false;
    if (!flushing.value && queue.value.length > 0) flush({ reason: 'tick' });
    return true;
  }

  /** Forget everything in memory (tests only — never wired to logout: L-I). */
  function clear() {
    resetMemory();
    resetBackoff();
    hydration = null;
    hydratedFor = undefined;
  }

  // ── A login that expired (L-D) ─────────────────────────────────────────────

  let probing = false;
  let lastProbeAt = 0;

  /** The session belongs to the collector whose taps these are (a plain profile read). */
  async function sameCollectorSignedIn() {
    try {
      return normalizeUserId(await probeSession()) === (hydratedFor ?? null);
    } catch {
      return false;
    }
  }

  /**
   * Ask whether the session holds (a plain profile read). A 401 reaches the
   * shared handler, which holds the collector on his day; an answer means the
   * session is alive — resume, or rebuild the auth store from the cookie
   * first. A tap or the tick ask at most every 15 s; the online flip,
   * visibility and pageshow ask at once.
   */
  async function probeAuth({ force = false } = {}) {
    if (probing || (!force && Date.now() - lastProbeAt < AUTH_PROBE_MS)) return;
    probing = true;
    lastProbeAt = Date.now();
    try {
      await probeSession();
      collectorNetwork().report(true);
      await sessionAlive();
    } catch (error) {
      collectorNetwork().report(error?.response?.status != null);
    } finally {
      probing = false;
    }
  }

  // The login is back → send what waited.
  watch(authPaused, (paused, wasPaused) => {
    if (!paused && wasPaused) flush({ reason: 'resume' });
  });

  // The hold asks what memory holds (a write may still be on its way to disk).
  registerLocalWork(
    (userId) =>
      hydratedFor === normalizeUserId(userId) &&
      (pendingCount.value > 0 || unsaved.size > 0 || useCollectorDayStore().serverDay != null),
  );

  // ── Triggers (L-C) ─────────────────────────────────────────────────────────

  function onVisibility() {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      flush({ reason: 'visible' });
    } else {
      // Leaving the screen is the last safe moment before an app kill.
      writeUnsaved();
    }
  }

  function onPageShow() {
    flush({ reason: 'pageshow' });
  }

  function onPageHide() {
    writeUnsaved();
  }

  /**
   * Arm the triggers (idempotent): App.vue after boot for a collector, and
   * every collector page on mount. The first call flushes what the phone kept.
   */
  function start() {
    if (started.value) return;
    started.value = true;
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
    if (typeof window !== 'undefined') {
      window.addEventListener('pageshow', onPageShow);
      window.addEventListener('pagehide', onPageHide);
    }
    flush({ reason: 'start' });
    // The collector's own photos (P2-C) follow the taps: same triggers, armed together.
    useCollectorPhotosStore().start();
    // So do the shops' invoices filed on the phone (collector-offline P1-A).
    useCollectorInvoicesStore().start();
  }

  // Back online → send what waited.
  watch(collectorNetwork().isOnline, (online) => {
    if (online) flush({ reason: 'online' });
  });

  // While anything waits: keep the shared detector running (it runs only
  // while a collector page is mounted otherwise) and tick every 60 s.
  let holdingDetector = false;
  watch(
    [pendingCount, started],
    ([waiting, armed]) => {
      const active = armed && waiting > 0;
      if (active && !holdingDetector) {
        collectorNetwork().start();
        holdingDetector = true;
      } else if (!active && holdingDetector) {
        collectorNetwork().stop();
        holdingDetector = false;
      }
      if (active && !tickTimer) {
        tickTimer = setInterval(() => flush({ reason: 'tick' }), TICK_MS);
      } else if (!active && tickTimer) {
        clearInterval(tickTimer);
        tickTimer = null;
      }
    },
    { immediate: true },
  );

  onScopeDispose(() => {
    clearTimers();
    clearInterval(tickTimer);
    tickTimer = null;
    if (holdingDetector) collectorNetwork().stop();
    holdingDetector = false;
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
    if (typeof window !== 'undefined') {
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('pagehide', onPageHide);
    }
  });

  return {
    queue,
    inflight,
    settled,
    pinned,
    conflicts,
    lastSyncAt,
    epoch,
    flushing,
    nextAttemptAt,
    started,
    pendingCount,
    authPaused,
    overlay,
    waitingByShop,
    ensureHydrated,
    enqueue,
    flush,
    start,
    pruneThrough,
    dismissConflict,
    announceAnswers,
    addCard,
    tapsWaiting,
    clear,
  };
});

/** A stored card back into the shape the day page renders. */
function cardOf(row) {
  const { userId: _userId, ...card } = row;
  return card;
}

/**
 * Told ONCE (spec 2.4): several taps on the same line can meet the same
 * leader decision (a queued «مش موجود», then «اتجاب», on a line she
 * cancelled) — one card per line and state, the latest tap's. A card that is
 * not about a line (a refusal's list; a shop's invoice, `addCard`) is its own:
 * keyed by its id, so two shops' cards never fold into one.
 */
function mergeCards(existing, incoming) {
  const key = (card) => (card.planItemId == null ? card.id : `${card.planItemId}:${card.state}`);
  const latest = new Map();
  for (const card of [...existing, ...incoming]) {
    latest.delete(key(card));
    latest.set(key(card), card);
  }
  return [...latest.values()];
}

/**
 * Refusals as cards: ONE per plan and reason (a closed day refuses every
 * replayed mark with the same words), listing each line once. The card keeps
 * a stable id per plan and reason, so a later refusal adds its lines to it.
 */
function refusalCards(refused, day, syncedAt, existing = []) {
  const byKey = new Map();
  for (const { action, reason } of refused) {
    const id = `refused:${action.planId ?? 'none'}:${reason}`;
    if (!byKey.has(id)) {
      const before = existing.find((card) => card.id === id);
      byKey.set(id, {
        id,
        planId: action.planId ?? null,
        state: 'refused',
        reason,
        type: action.type,
        planItemId: null,
        items: [...(before?.items ?? [])],
        syncedAt,
        seenAt: syncedAt,
      });
    }
    const card = byKey.get(id);
    if (card.items.some((item) => item.planItemId === action.planItemId)) continue;
    const hit = findLine(day, action.planItemId);
    card.items.push({
      planItemId: action.planItemId,
      productName: hit?.line?.productName ?? null,
      memberCode: hit?.line?.memberCode ?? null,
    });
  }
  return [...byKey.values()];
}

/**
 * One conflict card (spec 2.4), snapshotted from the line as the collector saw
 * it — the line itself is already pinned to the server's state.
 */
function conflictCard(action, answer, day, syncedAt) {
  const hit = findLine(day, action.planItemId);
  const shop = hit?.shop ?? (day?.shops ?? []).find((s) => String(s.supplierId) === String(action.payload?.supplierId));
  return {
    id: action.id,
    planId: action.planId ?? null,
    state: answer?.state ?? 'changed',
    reason: answer?.reason ?? null,
    type: action.type,
    planItemId: action.planItemId,
    productName: hit?.line?.productName ?? null,
    memberCode: hit?.line?.memberCode ?? null,
    shopName: shop?.name ?? null,
    shopKey: shop ? shopKey(shop) : null,
    currentOutcome: answer?.current?.outcome ?? null,
    current: answer?.current ?? null,
    syncedAt,
    seenAt: syncedAt,
  };
}
